export type CameraStatus = 'none' | 'A' | 'B' | 'both';
export type DotColor = 'blue' | 'yellow' | 'red' | 'streaming' | 'alarma';

export interface DotPerson {
  id: string;
  dotId: string;
  name: string;
  lat: number;
  lng: number;
  cameras: CameraStatus;
  lastUpdate: string;
  notes?: string;
  dotType?: 'streaming' | 'alarma';
  alarmReport?: string;
}

// Geographic zone polygon definition
export interface ZonePolygon {
  id: string;
  name: string;
  color: string;
  coordinates: [number, number][];
}

// Trajectory point for car-path feature
export interface TrajectoryPoint {
  lat: number;
  lng: number;
  timestamp: string;
  speed?: number;
}

export interface TrajectoryRoute {
  id: string;
  dotId: string;
  name: string;
  points: TrajectoryPoint[];
  color: string;
}

// Geographic zone polygons for Puebla City — geography-following shapes
export const ZONE_POLYGONS: ZonePolygon[] = [
  {
    id: 'zone-angelopolis',
    name: 'Angelópolis',
    color: '#6366f1',
    coordinates: [
      [19.0280, -98.2580],
      [19.0310, -98.2510],
      [19.0290, -98.2420],
      [19.0240, -98.2340],
      [19.0180, -98.2300],
      [19.0100, -98.2310],
      [19.0060, -98.2370],
      [19.0070, -98.2460],
      [19.0120, -98.2530],
      [19.0200, -98.2580],
    ],
  },
  {
    id: 'zone-cholula',
    name: 'Cholula',
    color: '#f59e0b',
    coordinates: [
      [19.0750, -98.3230],
      [19.0780, -98.3120],
      [19.0760, -98.3010],
      [19.0700, -98.2920],
      [19.0620, -98.2900],
      [19.0560, -98.2950],
      [19.0530, -98.3060],
      [19.0550, -98.3180],
      [19.0620, -98.3240],
      [19.0700, -98.3250],
    ],
  },
  {
    id: 'zone-buap',
    name: 'BUAP',
    color: '#10b981',
    coordinates: [
      [19.0450, -98.2120],
      [19.0460, -98.2050],
      [19.0430, -98.1960],
      [19.0380, -98.1890],
      [19.0300, -98.1870],
      [19.0240, -98.1900],
      [19.0220, -98.1980],
      [19.0250, -98.2070],
      [19.0310, -98.2120],
      [19.0390, -98.2130],
    ],
  },
  {
    id: 'zone-centro',
    name: 'Centro Histórico',
    color: '#ec4899',
    coordinates: [
      [19.0560, -98.2180],
      [19.0560, -98.2080],
      [19.0530, -98.1990],
      [19.0480, -98.1940],
      [19.0410, -98.1940],
      [19.0360, -98.1970],
      [19.0340, -98.2050],
      [19.0360, -98.2140],
      [19.0420, -98.2190],
      [19.0500, -98.2200],
    ],
  },
  {
    id: 'zone-norte',
    name: 'Zona Norte',
    color: '#06b6d4',
    coordinates: [
      [19.1080, -98.2400],
      [19.1100, -98.2250],
      [19.1060, -98.2100],
      [19.0980, -98.2000],
      [19.0880, -98.1960],
      [19.0780, -98.1980],
      [19.0700, -98.2060],
      [19.0700, -98.2200],
      [19.0760, -98.2340],
      [19.0860, -98.2400],
      [19.0980, -98.2420],
    ],
  },
  {
    id: 'zone-sur',
    name: 'Periférico Sur',
    color: '#f97316',
    coordinates: [
      [18.9840, -98.2380],
      [18.9860, -98.2260],
      [18.9840, -98.2140],
      [18.9780, -98.2060],
      [18.9700, -98.2020],
      [18.9620, -98.2040],
      [18.9580, -98.2120],
      [18.9600, -98.2260],
      [18.9660, -98.2360],
      [18.9760, -98.2400],
    ],
  },
  {
    id: 'zone-lomas',
    name: 'Lomas de Angelópolis',
    color: '#a855f7',
    coordinates: [
      [19.0040, -98.2760],
      [19.0060, -98.2650],
      [19.0020, -98.2540],
      [18.9950, -98.2460],
      [18.9870, -98.2440],
      [18.9800, -98.2480],
      [18.9780, -98.2580],
      [18.9820, -98.2700],
      [18.9900, -98.2760],
      [18.9980, -98.2780],
    ],
  },
  {
    id: 'zone-oriente',
    name: 'Zona Oriente',
    color: '#84cc16',
    coordinates: [
      [19.0400, -98.1580],
      [19.0420, -98.1480],
      [19.0390, -98.1380],
      [19.0320, -98.1320],
      [19.0220, -98.1320],
      [19.0140, -98.1380],
      [19.0120, -98.1480],
      [19.0150, -98.1580],
      [19.0240, -98.1640],
      [19.0340, -98.1640],
    ],
  },
];

// Sample trajectory routes across Puebla City
export const TRAJECTORY_ROUTES: TrajectoryRoute[] = [
  {
    id: 'route-1',
    dotId: '4214',
    name: 'Carlos Mendoza',
    color: '#3b82f6',
    points: [
      { lat: 19.0414, lng: -98.2063, timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(), speed: 0 },
      { lat: 19.0430, lng: -98.2100, timestamp: new Date(Date.now() - 55 * 60 * 1000).toISOString(), speed: 35 },
      { lat: 19.0460, lng: -98.2150, timestamp: new Date(Date.now() - 50 * 60 * 1000).toISOString(), speed: 42 },
      { lat: 19.0490, lng: -98.2180, timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(), speed: 38 },
      { lat: 19.0520, lng: -98.2200, timestamp: new Date(Date.now() - 40 * 60 * 1000).toISOString(), speed: 50 },
      { lat: 19.0550, lng: -98.2230, timestamp: new Date(Date.now() - 35 * 60 * 1000).toISOString(), speed: 45 },
      { lat: 19.0580, lng: -98.2260, timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), speed: 30 },
      { lat: 19.0600, lng: -98.2300, timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(), speed: 55 },
      { lat: 19.0620, lng: -98.2350, timestamp: new Date(Date.now() - 20 * 60 * 1000).toISOString(), speed: 48 },
      { lat: 19.0640, lng: -98.2400, timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(), speed: 40 },
      { lat: 19.0650, lng: -98.2450, timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(), speed: 35 },
      { lat: 19.0660, lng: -98.2500, timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(), speed: 20 },
      { lat: 19.0640, lng: -98.2550, timestamp: new Date(Date.now() - 2 * 60 * 1000).toISOString(), speed: 10 },
      { lat: 19.0620, lng: -98.2580, timestamp: new Date(Date.now() - 1 * 60 * 1000).toISOString(), speed: 5 },
      { lat: 19.0600, lng: -98.2600, timestamp: new Date(Date.now()).toISOString(), speed: 0 },
    ],
  },
  {
    id: 'route-2',
    dotId: '5530',
    name: 'Miguel Ángel Torres',
    color: '#f59e0b',
    points: [
      { lat: 19.0180, lng: -98.2400, timestamp: new Date(Date.now() - 90 * 60 * 1000).toISOString(), speed: 0 },
      { lat: 19.0210, lng: -98.2350, timestamp: new Date(Date.now() - 85 * 60 * 1000).toISOString(), speed: 30 },
      { lat: 19.0250, lng: -98.2300, timestamp: new Date(Date.now() - 80 * 60 * 1000).toISOString(), speed: 45 },
      { lat: 19.0280, lng: -98.2250, timestamp: new Date(Date.now() - 75 * 60 * 1000).toISOString(), speed: 50 },
      { lat: 19.0310, lng: -98.2200, timestamp: new Date(Date.now() - 70 * 60 * 1000).toISOString(), speed: 55 },
      { lat: 19.0320, lng: -98.2150, timestamp: new Date(Date.now() - 65 * 60 * 1000).toISOString(), speed: 40 },
      { lat: 19.0330, lng: -98.2100, timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(), speed: 35 },
      { lat: 19.0340, lng: -98.2050, timestamp: new Date(Date.now() - 55 * 60 * 1000).toISOString(), speed: 42 },
      { lat: 19.0350, lng: -98.2000, timestamp: new Date(Date.now() - 50 * 60 * 1000).toISOString(), speed: 38 },
      { lat: 19.0360, lng: -98.1980, timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(), speed: 0 },
    ],
  },
  {
    id: 'route-3',
    dotId: '3344',
    name: 'Eduardo Flores',
    color: '#10b981',
    points: [
      { lat: 19.0640, lng: -98.3050, timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(), speed: 0 },
      { lat: 19.0620, lng: -98.2950, timestamp: new Date(Date.now() - 40 * 60 * 1000).toISOString(), speed: 60 },
      { lat: 19.0580, lng: -98.2850, timestamp: new Date(Date.now() - 35 * 60 * 1000).toISOString(), speed: 65 },
      { lat: 19.0540, lng: -98.2750, timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), speed: 58 },
      { lat: 19.0500, lng: -98.2650, timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(), speed: 50 },
      { lat: 19.0460, lng: -98.2550, timestamp: new Date(Date.now() - 20 * 60 * 1000).toISOString(), speed: 45 },
      { lat: 19.0430, lng: -98.2450, timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(), speed: 40 },
      { lat: 19.0400, lng: -98.2380, timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(), speed: 35 },
      { lat: 19.0370, lng: -98.2350, timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(), speed: 20 },
      { lat: 19.0340, lng: -98.2350, timestamp: new Date(Date.now()).toISOString(), speed: 0 },
    ],
  },
  {
    id: 'route-4',
    dotId: '7788',
    name: 'Daniela Fuentes',
    color: '#a855f7',
    points: [
      { lat: 19.0750, lng: -98.2100, timestamp: new Date(Date.now() - 50 * 60 * 1000).toISOString(), speed: 0 },
      { lat: 19.0720, lng: -98.2050, timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(), speed: 28 },
      { lat: 19.0690, lng: -98.2000, timestamp: new Date(Date.now() - 40 * 60 * 1000).toISOString(), speed: 40 },
      { lat: 19.0660, lng: -98.1960, timestamp: new Date(Date.now() - 35 * 60 * 1000).toISOString(), speed: 48 },
      { lat: 19.0630, lng: -98.1920, timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), speed: 52 },
      { lat: 19.0600, lng: -98.1880, timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(), speed: 45 },
      { lat: 19.0570, lng: -98.1850, timestamp: new Date(Date.now() - 20 * 60 * 1000).toISOString(), speed: 38 },
      { lat: 19.0540, lng: -98.1830, timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(), speed: 30 },
      { lat: 19.0520, lng: -98.1810, timestamp: new Date(Date.now() - 8 * 60 * 1000).toISOString(), speed: 15 },
      { lat: 19.0510, lng: -98.1800, timestamp: new Date(Date.now()).toISOString(), speed: 0 },
    ],
  },
  {
    id: 'route-5',
    dotId: '9923',
    name: 'Paola Cervantes',
    color: '#ec4899',
    points: [
      { lat: 18.9900, lng: -98.2600, timestamp: new Date(Date.now() - 70 * 60 * 1000).toISOString(), speed: 0 },
      { lat: 18.9930, lng: -98.2550, timestamp: new Date(Date.now() - 63 * 60 * 1000).toISOString(), speed: 35 },
      { lat: 18.9960, lng: -98.2490, timestamp: new Date(Date.now() - 56 * 60 * 1000).toISOString(), speed: 50 },
      { lat: 19.0000, lng: -98.2430, timestamp: new Date(Date.now() - 49 * 60 * 1000).toISOString(), speed: 58 },
      { lat: 19.0040, lng: -98.2370, timestamp: new Date(Date.now() - 42 * 60 * 1000).toISOString(), speed: 62 },
      { lat: 19.0080, lng: -98.2310, timestamp: new Date(Date.now() - 35 * 60 * 1000).toISOString(), speed: 55 },
      { lat: 19.0110, lng: -98.2260, timestamp: new Date(Date.now() - 28 * 60 * 1000).toISOString(), speed: 44 },
      { lat: 19.0140, lng: -98.2220, timestamp: new Date(Date.now() - 20 * 60 * 1000).toISOString(), speed: 36 },
      { lat: 19.0160, lng: -98.2190, timestamp: new Date(Date.now() - 12 * 60 * 1000).toISOString(), speed: 22 },
      { lat: 19.0175, lng: -98.2170, timestamp: new Date(Date.now()).toISOString(), speed: 0 },
    ],
  },
  {
    id: 'route-6',
    dotId: '8890',
    name: 'Cristina Leal',
    color: '#f97316',
    points: [
      { lat: 18.9800, lng: -98.1800, timestamp: new Date(Date.now() - 55 * 60 * 1000).toISOString(), speed: 0 },
      { lat: 18.9830, lng: -98.1840, timestamp: new Date(Date.now() - 49 * 60 * 1000).toISOString(), speed: 30 },
      { lat: 18.9870, lng: -98.1890, timestamp: new Date(Date.now() - 43 * 60 * 1000).toISOString(), speed: 45 },
      { lat: 18.9910, lng: -98.1940, timestamp: new Date(Date.now() - 37 * 60 * 1000).toISOString(), speed: 52 },
      { lat: 18.9950, lng: -98.1990, timestamp: new Date(Date.now() - 31 * 60 * 1000).toISOString(), speed: 48 },
      { lat: 18.9990, lng: -98.2040, timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(), speed: 40 },
      { lat: 19.0030, lng: -98.2080, timestamp: new Date(Date.now() - 18 * 60 * 1000).toISOString(), speed: 33 },
      { lat: 19.0060, lng: -98.2110, timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(), speed: 20 },
      { lat: 19.0085, lng: -98.2130, timestamp: new Date(Date.now() - 4 * 60 * 1000).toISOString(), speed: 10 },
      { lat: 19.0100, lng: -98.2145, timestamp: new Date(Date.now()).toISOString(), speed: 0 },
    ],
  },
  {
    id: 'route-7',
    dotId: '2947',
    name: 'Sofía Hernández',
    color: '#06b6d4',
    points: [
      { lat: 19.0320, lng: -98.1980, timestamp: new Date(Date.now() - 40 * 60 * 1000).toISOString(), speed: 0 },
      { lat: 19.0350, lng: -98.2020, timestamp: new Date(Date.now() - 35 * 60 * 1000).toISOString(), speed: 32 },
      { lat: 19.0380, lng: -98.2070, timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), speed: 44 },
      { lat: 19.0400, lng: -98.2120, timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(), speed: 50 },
      { lat: 19.0420, lng: -98.2170, timestamp: new Date(Date.now() - 20 * 60 * 1000).toISOString(), speed: 46 },
      { lat: 19.0440, lng: -98.2220, timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(), speed: 38 },
      { lat: 19.0455, lng: -98.2260, timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(), speed: 28 },
      { lat: 19.0465, lng: -98.2290, timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(), speed: 15 },
      { lat: 19.0470, lng: -98.2310, timestamp: new Date(Date.now()).toISOString(), speed: 0 },
    ],
  },
  {
    id: 'route-8',
    dotId: '5567',
    name: 'Gabriela Ortiz',
    color: '#84cc16',
    points: [
      { lat: 19.0640, lng: -98.3050, timestamp: new Date(Date.now() - 65 * 60 * 1000).toISOString(), speed: 0 },
      { lat: 19.0610, lng: -98.2980, timestamp: new Date(Date.now() - 58 * 60 * 1000).toISOString(), speed: 42 },
      { lat: 19.0575, lng: -98.2900, timestamp: new Date(Date.now() - 51 * 60 * 1000).toISOString(), speed: 55 },
      { lat: 19.0540, lng: -98.2820, timestamp: new Date(Date.now() - 44 * 60 * 1000).toISOString(), speed: 60 },
      { lat: 19.0510, lng: -98.2740, timestamp: new Date(Date.now() - 37 * 60 * 1000).toISOString(), speed: 58 },
      { lat: 19.0480, lng: -98.2660, timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), speed: 50 },
      { lat: 19.0455, lng: -98.2590, timestamp: new Date(Date.now() - 22 * 60 * 1000).toISOString(), speed: 42 },
      { lat: 19.0435, lng: -98.2530, timestamp: new Date(Date.now() - 14 * 60 * 1000).toISOString(), speed: 30 },
      { lat: 19.0420, lng: -98.2480, timestamp: new Date(Date.now() - 6 * 60 * 1000).toISOString(), speed: 18 },
      { lat: 19.0410, lng: -98.2450, timestamp: new Date(Date.now()).toISOString(), speed: 0 },
    ],
  },
];

export const INITIAL_DOTS: DotPerson[] = [
  // Centro Histórico cluster
  { id: 'dot-4214', dotId: '4214', name: 'Carlos Mendoza', lat: 19.0414, lng: -98.2063, cameras: 'both', lastUpdate: new Date(Date.now() - 8 * 60 * 1000).toISOString() },
  { id: 'dot-3871', dotId: '3871', name: 'Laura Reyes', lat: 19.0502, lng: -98.2150, cameras: 'A', lastUpdate: new Date(Date.now() - 12 * 60 * 1000).toISOString() },
  { id: 'dot-6682', dotId: '6682', name: 'Ana Martínez', lat: 19.0460, lng: -98.1900, cameras: 'A', lastUpdate: new Date(Date.now() - 5 * 60 * 1000).toISOString() },
  { id: 'dot-8821', dotId: '8821', name: 'Valeria Ruiz', lat: 19.0420, lng: -98.2050, cameras: 'A', lastUpdate: new Date(Date.now() - 20 * 60 * 1000).toISOString() },
  { id: 'dot-1122', dotId: '1122', name: 'María Jiménez', lat: 19.0440, lng: -98.2080, cameras: 'A', lastUpdate: new Date(Date.now() - 25 * 60 * 1000).toISOString() },
  { id: 'dot-3344', dotId: '3344', name: 'Eduardo Flores', lat: 19.0430, lng: -98.2040, cameras: 'both', lastUpdate: new Date(Date.now() - 3 * 60 * 1000).toISOString() },
  { id: 'dot-3398', dotId: '3398', name: 'Isabel Cruz', lat: 19.0450, lng: -98.2070, cameras: 'both', lastUpdate: new Date(Date.now() - 7 * 60 * 1000).toISOString() },
  { id: 'dot-5512', dotId: '5512', name: 'Rodrigo Soto', lat: 19.0408, lng: -98.2090, cameras: 'none', lastUpdate: new Date(Date.now() - 18 * 60 * 1000).toISOString() },
  { id: 'dot-7723', dotId: '7723', name: 'Natalia Campos', lat: 19.0425, lng: -98.2030, cameras: 'B', lastUpdate: new Date(Date.now() - 10 * 60 * 1000).toISOString() },

  // Angelópolis cluster
  { id: 'dot-5530', dotId: '5530', name: 'Miguel Ángel Torres', lat: 19.0180, lng: -98.2400, cameras: 'both', lastUpdate: new Date(Date.now() - 45 * 60 * 1000).toISOString() },
  { id: 'dot-4409', dotId: '4409', name: 'Fernando Díaz', lat: 19.0190, lng: -98.2380, cameras: 'B', lastUpdate: new Date(Date.now() - 55 * 60 * 1000).toISOString() },
  { id: 'dot-6690', dotId: '6690', name: 'Arturo Vega', lat: 19.0170, lng: -98.2420, cameras: 'B', lastUpdate: new Date(Date.now() - 50 * 60 * 1000).toISOString() },
  { id: 'dot-9945', dotId: '9945', name: 'Alejandro Peña', lat: 19.0160, lng: -98.2440, cameras: 'A', lastUpdate: new Date(Date.now() - 88 * 60 * 1000).toISOString() },
  { id: 'dot-2211', dotId: '2211', name: 'Sofía Delgado', lat: 19.0200, lng: -98.2360, cameras: 'none', lastUpdate: new Date(Date.now() - 35 * 60 * 1000).toISOString() },
  { id: 'dot-8834', dotId: '8834', name: 'Luis Gutiérrez', lat: 19.0175, lng: -98.2410, cameras: 'none', lastUpdate: new Date(Date.now() - 67 * 60 * 1000).toISOString() },

  // BUAP / San Manuel cluster
  { id: 'dot-2947', dotId: '2947', name: 'Sofía Hernández', lat: 19.0320, lng: -98.1980, cameras: 'B', lastUpdate: new Date(Date.now() - 38 * 60 * 1000).toISOString() },
  { id: 'dot-7103', dotId: '7103', name: 'Javier López', lat: 19.0310, lng: -98.1960, cameras: 'none', lastUpdate: new Date(Date.now() - 75 * 60 * 1000).toISOString() },
  { id: 'dot-2201', dotId: '2201', name: 'Héctor Morales', lat: 19.0330, lng: -98.1990, cameras: 'B', lastUpdate: new Date(Date.now() - 130 * 60 * 1000).toISOString() },
  { id: 'dot-4455', dotId: '4455', name: 'Camila Ríos', lat: 19.0340, lng: -98.1970, cameras: 'A', lastUpdate: new Date(Date.now() - 22 * 60 * 1000).toISOString() },
  { id: 'dot-6631', dotId: '6631', name: 'Tomás Vargas', lat: 19.0300, lng: -98.2000, cameras: 'both', lastUpdate: new Date(Date.now() - 14 * 60 * 1000).toISOString() },

  // Cholula cluster
  { id: 'dot-5567', dotId: '5567', name: 'Gabriela Ortiz', lat: 19.0640, lng: -98.3050, cameras: 'both', lastUpdate: new Date(Date.now() - 15 * 60 * 1000).toISOString() },
  { id: 'dot-7756', dotId: '7756', name: 'Patricia Sánchez', lat: 19.0650, lng: -98.3020, cameras: 'A', lastUpdate: new Date(Date.now() - 42 * 60 * 1000).toISOString() },
  { id: 'dot-1358', dotId: '1358', name: 'Roberto Castillo', lat: 19.0630, lng: -98.3070, cameras: 'both', lastUpdate: new Date(Date.now() - 110 * 60 * 1000).toISOString() },
  { id: 'dot-9012', dotId: '9012', name: 'Claudia Ramírez', lat: 19.0660, lng: -98.3040, cameras: 'none', lastUpdate: new Date(Date.now() - 95 * 60 * 1000).toISOString() },
  { id: 'dot-3312', dotId: '3312', name: 'Andrés Luna', lat: 19.0620, lng: -98.3080, cameras: 'B', lastUpdate: new Date(Date.now() - 28 * 60 * 1000).toISOString() },

  // Zona Norte cluster
  { id: 'dot-7788', dotId: '7788', name: 'Daniela Fuentes', lat: 19.0750, lng: -98.2100, cameras: 'A', lastUpdate: new Date(Date.now() - 6 * 60 * 1000).toISOString() },
  { id: 'dot-2234', dotId: '2234', name: 'Ernesto Blanco', lat: 19.0760, lng: -98.2080, cameras: 'both', lastUpdate: new Date(Date.now() - 33 * 60 * 1000).toISOString() },
  { id: 'dot-5599', dotId: '5599', name: 'Verónica Aguilar', lat: 19.0740, lng: -98.2120, cameras: 'none', lastUpdate: new Date(Date.now() - 72 * 60 * 1000).toISOString() },
  { id: 'dot-8867', dotId: '8867', name: 'Marcos Herrera', lat: 19.0770, lng: -98.2060, cameras: 'B', lastUpdate: new Date(Date.now() - 16 * 60 * 1000).toISOString() },

  // Huexotitla / Lomas cluster
  { id: 'dot-4477', dotId: '4477', name: 'Diana Vargas', lat: 19.0460, lng: -98.2300, cameras: 'none', lastUpdate: new Date(Date.now() - 32 * 60 * 1000).toISOString() },
  { id: 'dot-1189', dotId: '1189', name: 'Sergio Medina', lat: 19.0470, lng: -98.2320, cameras: 'A', lastUpdate: new Date(Date.now() - 48 * 60 * 1000).toISOString() },
  { id: 'dot-6645', dotId: '6645', name: 'Lorena Paredes', lat: 19.0450, lng: -98.2280, cameras: 'both', lastUpdate: new Date(Date.now() - 9 * 60 * 1000).toISOString() },
  { id: 'dot-3367', dotId: '3367', name: 'Óscar Reina', lat: 19.0480, lng: -98.2340, cameras: 'B', lastUpdate: new Date(Date.now() - 120 * 60 * 1000).toISOString() },

  // Lomas de Angelópolis cluster
  { id: 'dot-9923', dotId: '9923', name: 'Paola Cervantes', lat: 18.9900, lng: -98.2600, cameras: 'A', lastUpdate: new Date(Date.now() - 11 * 60 * 1000).toISOString() },
  { id: 'dot-2278', dotId: '2278', name: 'Iván Montes', lat: 18.9910, lng: -98.2580, cameras: 'none', lastUpdate: new Date(Date.now() - 58 * 60 * 1000).toISOString() },
  { id: 'dot-7701', dotId: '7701', name: 'Alicia Mora', lat: 18.9890, lng: -98.2620, cameras: 'both', lastUpdate: new Date(Date.now() - 85 * 60 * 1000).toISOString() },
  { id: 'dot-5545', dotId: '5545', name: 'Benjamín Salinas', lat: 18.9920, lng: -98.2560, cameras: 'B', lastUpdate: new Date(Date.now() - 27 * 60 * 1000).toISOString() },

  // Valsequillo / Sur cluster
  { id: 'dot-8890', dotId: '8890', name: 'Cristina Leal', lat: 18.9800, lng: -98.1800, cameras: 'A', lastUpdate: new Date(Date.now() - 4 * 60 * 1000).toISOString() },
  { id: 'dot-1156', dotId: '1156', name: 'Guillermo Ponce', lat: 18.9810, lng: -98.1820, cameras: 'none', lastUpdate: new Date(Date.now() - 62 * 60 * 1000).toISOString() },
  { id: 'dot-4423', dotId: '4423', name: 'Rebeca Trejo', lat: 18.9790, lng: -98.1780, cameras: 'both', lastUpdate: new Date(Date.now() - 40 * 60 * 1000).toISOString() },

  // Pedregal / Oriente cluster
  { id: 'dot-6678', dotId: '6678', name: 'Mauricio Espinoza', lat: 19.0100, lng: -98.2200, cameras: 'B', lastUpdate: new Date(Date.now() - 19 * 60 * 1000).toISOString() },
  { id: 'dot-3390', dotId: '3390', name: 'Adriana Núñez', lat: 19.0110, lng: -98.2180, cameras: 'A', lastUpdate: new Date(Date.now() - 44 * 60 * 1000).toISOString() },
  { id: 'dot-9956', dotId: '9956', name: 'Felipe Guerrero', lat: 19.0090, lng: -98.2220, cameras: 'none', lastUpdate: new Date(Date.now() - 100 * 60 * 1000).toISOString() },

  // Scattered / Oriente
  { id: 'dot-7712', dotId: '7712', name: 'Silvia Ibarra', lat: 19.0530, lng: -98.1840, cameras: 'A', lastUpdate: new Date(Date.now() - 17 * 60 * 1000).toISOString() },
  { id: 'dot-2256', dotId: '2256', name: 'Ramón Castañeda', lat: 19.0580, lng: -98.1780, cameras: 'both', lastUpdate: new Date(Date.now() - 53 * 60 * 1000).toISOString() },
  { id: 'dot-5578', dotId: '5578', name: 'Yolanda Bravo', lat: 19.0250, lng: -98.2100, cameras: 'B', lastUpdate: new Date(Date.now() - 78 * 60 * 1000).toISOString() },
  { id: 'dot-8845', dotId: '8845', name: 'Nicolás Fuentes', lat: 19.0600, lng: -98.1970, cameras: 'none', lastUpdate: new Date(Date.now() - 140 * 60 * 1000).toISOString() },
  { id: 'dot-1134', dotId: '1134', name: 'Esperanza Rueda', lat: 19.0390, lng: -98.2280, cameras: 'A', lastUpdate: new Date(Date.now() - 29 * 60 * 1000).toISOString() },
  { id: 'dot-4401', dotId: '4401', name: 'Ignacio Salazar', lat: 19.0710, lng: -98.2230, cameras: 'both', lastUpdate: new Date(Date.now() - 2 * 60 * 1000).toISOString() },

  // Extra demo data — Zona Universitaria
  { id: 'dot-1201', dotId: '1201', name: 'Karla Domínguez', lat: 19.0355, lng: -98.2010, cameras: 'A', lastUpdate: new Date(Date.now() - 6 * 60 * 1000).toISOString() },
  { id: 'dot-3302', dotId: '3302', name: 'Héctor Villanueva', lat: 19.0365, lng: -98.2025, cameras: 'both', lastUpdate: new Date(Date.now() - 13 * 60 * 1000).toISOString() },
  { id: 'dot-5503', dotId: '5503', name: 'Nadia Esquivel', lat: 19.0375, lng: -98.2040, cameras: 'B', lastUpdate: new Date(Date.now() - 21 * 60 * 1000).toISOString() },
  { id: 'dot-7704', dotId: '7704', name: 'Óscar Palomino', lat: 19.0385, lng: -98.2055, cameras: 'A', lastUpdate: new Date(Date.now() - 37 * 60 * 1000).toISOString() },
  { id: 'dot-9905', dotId: '9905', name: 'Miriam Contreras', lat: 19.0395, lng: -98.2070, cameras: 'both', lastUpdate: new Date(Date.now() - 8 * 60 * 1000).toISOString() },

  // Extra demo data — Zona Industrial
  { id: 'dot-2102', dotId: '2102', name: 'Raúl Espinosa', lat: 19.0820, lng: -98.2300, cameras: 'A', lastUpdate: new Date(Date.now() - 5 * 60 * 1000).toISOString() },
  { id: 'dot-4403', dotId: '4403', name: 'Cecilia Montoya', lat: 19.0830, lng: -98.2280, cameras: 'B', lastUpdate: new Date(Date.now() - 47 * 60 * 1000).toISOString() },
  { id: 'dot-6604', dotId: '6604', name: 'Jaime Sandoval', lat: 19.0840, lng: -98.2260, cameras: 'both', lastUpdate: new Date(Date.now() - 3 * 60 * 1000).toISOString() },
  { id: 'dot-8805', dotId: '8805', name: 'Leticia Cárdenas', lat: 19.0850, lng: -98.2240, cameras: 'A', lastUpdate: new Date(Date.now() - 90 * 60 * 1000).toISOString() },
  { id: 'dot-1106', dotId: '1106', name: 'Víctor Alvarado', lat: 19.0860, lng: -98.2220, cameras: 'none', lastUpdate: new Date(Date.now() - 115 * 60 * 1000).toISOString() },

  // Extra demo data — San Andrés Cholula
  { id: 'dot-3307', dotId: '3307', name: 'Fernanda Ríos', lat: 19.0580, lng: -98.3120, cameras: 'B', lastUpdate: new Date(Date.now() - 11 * 60 * 1000).toISOString() },
  { id: 'dot-5508', dotId: '5508', name: 'Armando Téllez', lat: 19.0590, lng: -98.3100, cameras: 'A', lastUpdate: new Date(Date.now() - 26 * 60 * 1000).toISOString() },
  { id: 'dot-7709', dotId: '7709', name: 'Graciela Mendez', lat: 19.0600, lng: -98.3080, cameras: 'both', lastUpdate: new Date(Date.now() - 4 * 60 * 1000).toISOString() },
  { id: 'dot-9910', dotId: '9910', name: 'Enrique Barrera', lat: 19.0610, lng: -98.3060, cameras: 'A', lastUpdate: new Date(Date.now() - 70 * 60 * 1000).toISOString() },

  // Zona Poniente — activos
  { id: 'dot-1301', dotId: '1301', name: 'Alejandra Vidal', lat: 19.0490, lng: -98.2500, cameras: 'A', lastUpdate: new Date(Date.now() - 7 * 60 * 1000).toISOString() },
  { id: 'dot-2302', dotId: '2302', name: 'Rubén Castillo', lat: 19.0510, lng: -98.2520, cameras: 'both', lastUpdate: new Date(Date.now() - 14 * 60 * 1000).toISOString() },
  { id: 'dot-3303', dotId: '3303', name: 'Mónica Serrano', lat: 19.0530, lng: -98.2480, cameras: 'B', lastUpdate: new Date(Date.now() - 2 * 60 * 1000).toISOString() },
  { id: 'dot-4304', dotId: '4304', name: 'Gustavo Pedraza', lat: 19.0470, lng: -98.2540, cameras: 'none', lastUpdate: new Date(Date.now() - 20 * 60 * 1000).toISOString() },

  // Zona Poniente — desconectados
  { id: 'dot-5305', dotId: '5305', name: 'Liliana Ochoa', lat: 19.0550, lng: -98.2560, cameras: 'A', lastUpdate: new Date(Date.now() - 35 * 60 * 1000).toISOString() },
  { id: 'dot-6306', dotId: '6306', name: 'Aurelio Pinto', lat: 19.0560, lng: -98.2580, cameras: 'none', lastUpdate: new Date(Date.now() - 50 * 60 * 1000).toISOString() },

  // Zona Poniente — streaming
  { id: 'dot-7307', dotId: '7307', name: 'Consuelo Ávila', lat: 19.0480, lng: -98.2600, cameras: 'B', lastUpdate: new Date(Date.now() - 95 * 60 * 1000).toISOString(), dotType: 'streaming' },
  { id: 'dot-8308', dotId: '8308', name: 'Horacio Téllez', lat: 19.0500, lng: -98.2620, cameras: 'none', lastUpdate: new Date(Date.now() - 150 * 60 * 1000).toISOString(), dotType: 'streaming' },

  // Zona Oriente / Tehuacán corridor — activos
  { id: 'dot-1401', dotId: '1401', name: 'Beatriz Lozano', lat: 19.0200, lng: -98.1600, cameras: 'A', lastUpdate: new Date(Date.now() - 9 * 60 * 1000).toISOString() },
  { id: 'dot-2402', dotId: '2402', name: 'Ernesto Quiñones', lat: 19.0220, lng: -98.1580, cameras: 'both', lastUpdate: new Date(Date.now() - 18 * 60 * 1000).toISOString() },
  { id: 'dot-3403', dotId: '3403', name: 'Mariana Solís', lat: 19.0240, lng: -98.1560, cameras: 'B', lastUpdate: new Date(Date.now() - 5 * 60 * 1000).toISOString() },

  // Zona Oriente — desconectados
  { id: 'dot-4404', dotId: '4404', name: 'Rodrigo Ibáñez', lat: 19.0260, lng: -98.1540, cameras: 'A', lastUpdate: new Date(Date.now() - 40 * 60 * 1000).toISOString() },
  { id: 'dot-5405', dotId: '5405', name: 'Susana Meza', lat: 19.0280, lng: -98.1520, cameras: 'none', lastUpdate: new Date(Date.now() - 55 * 60 * 1000).toISOString() },

  // Zona Oriente — streaming
  { id: 'dot-6406', dotId: '6406', name: 'Ángel Domínguez', lat: 19.0300, lng: -98.1500, cameras: 'B', lastUpdate: new Date(Date.now() - 120 * 60 * 1000).toISOString(), dotType: 'streaming' },
  { id: 'dot-7407', dotId: '7407', name: 'Pilar Estrada', lat: 19.0320, lng: -98.1480, cameras: 'none', lastUpdate: new Date(Date.now() - 180 * 60 * 1000).toISOString(), dotType: 'streaming' },

  // Zona Norte ampliada — activos
  { id: 'dot-1501', dotId: '1501', name: 'Tomás Aguilera', lat: 19.0900, lng: -98.2150, cameras: 'A', lastUpdate: new Date(Date.now() - 12 * 60 * 1000).toISOString() },
  { id: 'dot-2502', dotId: '2502', name: 'Lucía Peñaloza', lat: 19.0920, lng: -98.2130, cameras: 'both', lastUpdate: new Date(Date.now() - 6 * 60 * 1000).toISOString() },
  { id: 'dot-3503', dotId: '3503', name: 'Gerardo Fuentes', lat: 19.0940, lng: -98.2110, cameras: 'B', lastUpdate: new Date(Date.now() - 22 * 60 * 1000).toISOString() },

  // Zona Norte ampliada — desconectados
  { id: 'dot-4504', dotId: '4504', name: 'Norma Villalba', lat: 19.0960, lng: -98.2090, cameras: 'A', lastUpdate: new Date(Date.now() - 45 * 60 * 1000).toISOString() },
  { id: 'dot-5505', dotId: '5505', name: 'Claudio Ramos', lat: 19.0980, lng: -98.2070, cameras: 'none', lastUpdate: new Date(Date.now() - 58 * 60 * 1000).toISOString() },

  // Zona Norte ampliada — streaming
  { id: 'dot-6506', dotId: '6506', name: 'Elvira Sandoval', lat: 19.1000, lng: -98.2050, cameras: 'B', lastUpdate: new Date(Date.now() - 130 * 60 * 1000).toISOString(), dotType: 'streaming' },
  { id: 'dot-7507', dotId: '7507', name: 'Patricio Reyes', lat: 19.1020, lng: -98.2030, cameras: 'none', lastUpdate: new Date(Date.now() - 200 * 60 * 1000).toISOString(), dotType: 'streaming' },

  // Periférico Sur — activos
  { id: 'dot-1601', dotId: '1601', name: 'Ximena Guerrero', lat: 18.9700, lng: -98.2200, cameras: 'A', lastUpdate: new Date(Date.now() - 8 * 60 * 1000).toISOString() },
  { id: 'dot-2602', dotId: '2602', name: 'Dante Moreno', lat: 18.9720, lng: -98.2180, cameras: 'both', lastUpdate: new Date(Date.now() - 16 * 60 * 1000).toISOString() },
  { id: 'dot-3603', dotId: '3603', name: 'Inés Cabrera', lat: 18.9740, lng: -98.2160, cameras: 'B', lastUpdate: new Date(Date.now() - 3 * 60 * 1000).toISOString() },

  // Periférico Sur — desconectados
  { id: 'dot-4604', dotId: '4604', name: 'Leandro Vázquez', lat: 18.9760, lng: -98.2140, cameras: 'none', lastUpdate: new Date(Date.now() - 38 * 60 * 1000).toISOString() },
  { id: 'dot-5605', dotId: '5605', name: 'Rosario Peña', lat: 18.9780, lng: -98.2120, cameras: 'A', lastUpdate: new Date(Date.now() - 52 * 60 * 1000).toISOString() },

  // Periférico Sur — streaming
  { id: 'dot-6606', dotId: '6606', name: 'Alfredo Nava', lat: 18.9680, lng: -98.2220, cameras: 'B', lastUpdate: new Date(Date.now() - 105 * 60 * 1000).toISOString(), dotType: 'streaming' },
  { id: 'dot-7607', dotId: '7607', name: 'Catalina Briones', lat: 18.9660, lng: -98.2240, cameras: 'none', lastUpdate: new Date(Date.now() - 160 * 60 * 1000).toISOString(), dotType: 'streaming' },

  // Corredor Atlixco — activos
  { id: 'dot-1701', dotId: '1701', name: 'Sebastián Mora', lat: 19.0050, lng: -98.2700, cameras: 'A', lastUpdate: new Date(Date.now() - 10 * 60 * 1000).toISOString() },
  { id: 'dot-2702', dotId: '2702', name: 'Valeria Espino', lat: 19.0070, lng: -98.2720, cameras: 'both', lastUpdate: new Date(Date.now() - 24 * 60 * 1000).toISOString() },

  // Corredor Atlixco — desconectados
  { id: 'dot-3703', dotId: '3703', name: 'Rodrigo Palma', lat: 19.0090, lng: -98.2740, cameras: 'B', lastUpdate: new Date(Date.now() - 43 * 60 * 1000).toISOString() },

  // Corredor Atlixco — streaming
  { id: 'dot-4704', dotId: '4704', name: 'Elisa Montiel', lat: 19.0030, lng: -98.2680, cameras: 'none', lastUpdate: new Date(Date.now() - 135 * 60 * 1000).toISOString(), dotType: 'streaming' },
  { id: 'dot-5705', dotId: '5705', name: 'Marcos Villanueva', lat: 19.0010, lng: -98.2660, cameras: 'A', lastUpdate: new Date(Date.now() - 175 * 60 * 1000).toISOString(), dotType: 'streaming' },

  // Demo cluster — Centro Histórico
  { id: 'dot-c003', dotId: 'C003', name: 'Unidad Activa 1', lat: 19.0420, lng: -98.2060, cameras: 'A', lastUpdate: new Date(Date.now() - 5 * 60 * 1000).toISOString() },
  { id: 'dot-c004', dotId: 'C004', name: 'Unidad Activa 2', lat: 19.0415, lng: -98.2058, cameras: 'both', lastUpdate: new Date(Date.now() - 8 * 60 * 1000).toISOString() },
  { id: 'dot-c008', dotId: 'C008', name: 'Unidad Activa 6', lat: 19.0421, lng: -98.2072, cameras: 'A', lastUpdate: new Date(Date.now() - 18 * 60 * 1000).toISOString() },
  { id: 'dot-c009', dotId: 'C009', name: 'Unidad Desc. 1', lat: 19.0417, lng: -98.2075, cameras: 'none', lastUpdate: new Date(Date.now() - 35 * 60 * 1000).toISOString() },
  { id: 'dot-c010', dotId: 'C010', name: 'Unidad Desc. 2', lat: 19.0411, lng: -98.2066, cameras: 'A', lastUpdate: new Date(Date.now() - 50 * 60 * 1000).toISOString() },
  { id: 'dot-c016', dotId: 'C016', name: 'Unidad Activa 7', lat: 19.0414, lng: -98.2053, cameras: 'none', lastUpdate: new Date(Date.now() - 7 * 60 * 1000).toISOString() },
  { id: 'dot-c017', dotId: 'C017', name: 'Unidad Activa 8', lat: 19.0426, lng: -98.2067, cameras: 'B', lastUpdate: new Date(Date.now() - 9 * 60 * 1000).toISOString() },
  { id: 'dot-c018', dotId: 'C018', name: 'Unidad Activa 9', lat: 19.0407, lng: -98.2061, cameras: 'A', lastUpdate: new Date(Date.now() - 11 * 60 * 1000).toISOString() },
  { id: 'dot-c019', dotId: 'C019', name: 'Unidad Activa 10', lat: 19.0425, lng: -98.2078, cameras: 'both', lastUpdate: new Date(Date.now() - 14 * 60 * 1000).toISOString() },

  // ── ALARMA dots — one per zone ──────────────────────────────────────────
  // Angelópolis — alarma
  { id: 'dot-alm-01', dotId: 'ALM01', name: 'Alarma Angelópolis', lat: 19.0180, lng: -98.2430, cameras: 'none', lastUpdate: new Date(Date.now() - 2 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Robo a casa habitación. Se reporta sustracción de bienes en domicilio particular. Sospechoso masculino, complexión robusta, vestimenta oscura. Ciudadano peligroso armado a la fuga.' },
  // Cholula — alarma
  { id: 'dot-alm-02', dotId: 'ALM02', name: 'Alarma Cholula', lat: 19.0650, lng: -98.3060, cameras: 'none', lastUpdate: new Date(Date.now() - 1 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Sospechoso detectado merodeando zona escolar. Individuo sin identificación, actitud evasiva. Se solicita presencia de unidad para revisión y posible detención preventiva.' },
  // BUAP — alarma
  { id: 'dot-alm-03', dotId: 'ALM03', name: 'Alarma BUAP', lat: 19.0330, lng: -98.1970, cameras: 'none', lastUpdate: new Date(Date.now() - 3 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Ciudadano peligroso reportado en inmediaciones de campus universitario. Antecedentes de agresión. Porta objeto contundente. Se requiere apoyo inmediato de patrulla.' },
  // Zona Norte — alarma
  { id: 'dot-alm-04', dotId: 'ALM04', name: 'Alarma Zona Norte', lat: 19.0800, lng: -98.2150, cameras: 'none', lastUpdate: new Date(Date.now() - 2 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Clave 10-0: Persecución en curso sobre Blvd. Norte. Vehículo sospechoso modelo sedán color gris, placas sin identificar. Unidades en seguimiento activo.' },
  // Periférico Sur — alarma
  { id: 'dot-alm-05', dotId: 'ALM05', name: 'Alarma Periférico Sur', lat: 18.9720, lng: -98.2200, cameras: 'none', lastUpdate: new Date(Date.now() - 1 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Accidente automovilístico en Periférico Ecológico. Colisión entre dos vehículos particulares. Dos personas lesionadas, se solicita ambulancia y cierre de carril derecho.' },
  // Lomas de Angelópolis — alarma
  { id: 'dot-alm-06', dotId: 'ALM06', name: 'Alarma Lomas', lat: 18.9900, lng: -98.2620, cameras: 'none', lastUpdate: new Date(Date.now() - 4 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Robo a transeúnte en fraccionamiento residencial. Víctima reporta despojo de pertenencias a mano armada. Sospechoso huyó a pie hacia zona boscosa.' },
  // Zona Oriente — alarma
  { id: 'dot-alm-07', dotId: 'ALM07', name: 'Alarma Zona Oriente', lat: 19.0270, lng: -98.1490, cameras: 'none', lastUpdate: new Date(Date.now() - 2 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Manifestación y bloqueo vial en Av. Oriente. Grupo de aproximadamente 40 personas obstaculiza el tránsito. Se requiere negociación y control de multitudes.' },
  // Huexotitla / Poniente — alarma
  { id: 'dot-alm-08', dotId: 'ALM08', name: 'Alarma Zona Poniente', lat: 19.0510, lng: -98.2540, cameras: 'none', lastUpdate: new Date(Date.now() - 3 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Agresión con arma de fuego en zona poniente. Clave 07. Herido de bala en vía pública. Agresor identificado como ciudadano peligroso con antecedentes penales.' },

  // ── 4 ALARMA dots clustered near Centro Histórico cluster ───────────────
  { id: 'dot-alm-c1', dotId: 'AC01', name: 'Alarma Centro 1', lat: 19.0432, lng: -98.2055, cameras: 'none', lastUpdate: new Date(Date.now() - 1 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Robo a negocio en Centro Histórico. Tres sospechosos ingresaron a establecimiento comercial. Uno porta arma blanca. Cámaras de vigilancia en operación.' },
  { id: 'dot-alm-c2', dotId: 'AC02', name: 'Alarma Centro 2', lat: 19.0418, lng: -98.2048, cameras: 'none', lastUpdate: new Date(Date.now() - 2 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Sospechoso con actitud violenta en Plaza Mayor. Ciudadano peligroso con historial de agresiones. Amenaza a transeúntes. Se solicita unidad de contención inmediata.' },
  { id: 'dot-alm-c3', dotId: 'AC03', name: 'Alarma Centro 3', lat: 19.0440, lng: -98.2062, cameras: 'none', lastUpdate: new Date(Date.now() - 1 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Clave 10-1: Oficial necesita ayuda urgente. Unidad C03 reporta situación de riesgo en Calle 5 de Mayo. Código rojo local activado. Apoyo inmediato requerido.' },
  { id: 'dot-alm-c4', dotId: 'AC04', name: 'Alarma Centro 4', lat: 19.0426, lng: -98.2042, cameras: 'none', lastUpdate: new Date(Date.now() - 3 * 60 * 1000).toISOString(), dotType: 'alarma', alarmReport: 'Robo a casa habitación en Barrio de Analco. Vecinos reportan actividad sospechosa. Posible banda organizada operando en la zona. Patrullaje preventivo solicitado.' },
];

export function getDotColor(lastUpdate: string): DotColor {
  const diffMs = Date.now() - new Date(lastUpdate).getTime();
  const diffMin = diffMs / 60000;
  if (diffMin < 30) return 'blue';
  if (diffMin < 60) return 'yellow';
  return 'red';
}

export function isSpecialDotType(dot: DotPerson): DotColor | null {
  if (dot.dotType === 'alarma') return 'alarma';
  if (dot.dotType === 'streaming') return 'streaming';
  return null;
}

export function getEffectiveDotColor(dot: DotPerson): DotColor {
  const special = isSpecialDotType(dot);
  if (special) return special;
  return getDotColor(dot.lastUpdate);
}

export function formatRelativeTime(lastUpdate: string): string {
  const diffMs = Date.now() - new Date(lastUpdate).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Hace menos de 1 min';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const hrs = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return mins > 0 ? `Hace ${hrs}h ${mins}min` : `Hace ${hrs}h`;
}

export function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function getCameraLabel(cameras: CameraStatus): string {
  if (cameras === 'both') return 'Cámara A + B';
  if (cameras === 'A') return 'Cámara A';
  if (cameras === 'B') return 'Cámara B';
  return 'Sin cámara';
}

export function getColorLabel(color: DotColor): string {
  if (color === 'alarma') return 'Alarma';
  if (color === 'blue') return 'Activo';
  if (color === 'yellow') return 'Desconectado';
  if (color === 'red') return 'Fuera de línea';
  if (color === 'streaming') return 'Streaming';
  return 'Desconocido';
}

export function generateDotId(existing: string[]): string {
  let id: string;
  do {
    id = String(Math.floor(1000 + Math.random() * 9000));
  } while (existing.includes(id));
  return id;
}